import type {
  Question,
  ExamTypeConfig,
  ExamSection,
  Paper,
  PaperSection,
  QuestionType,
  AnswerSheetBySection,
  SectionQuestionTypeConstraint,
} from '../types';
import { samplePreferringPassageClusters } from './passageClusterSample';

/**
 * When section.questionCount is unset, do not pretend this is an official full paper.
 * Cap inventory sampling so accidental "all questions" does not look like a full mock.
 */
const UNCONFIGURED_SECTION_QUESTION_CAP = 40;

/**
 * Fisher-Yates 洗牌
 */
function shuffle<T>(array: T[]): T[] {
  const out = [...array];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function resolveSectionConstraints(section: ExamSection): SectionQuestionTypeConstraint[] {
  if (section.questionTypeConstraints && section.questionTypeConstraints.length > 0) {
    return section.questionTypeConstraints;
  }

  return (section.questionTypes ?? []).map((type) => ({
    questionTypeId: type,
  }));
}

function resolveAllowedBaseTypes(
  section: ExamSection,
  config: ExamTypeConfig
): Set<QuestionType> {
  const allowed = new Set<QuestionType>();
  const definitions = config.questionTypeDefinitions ?? {};
  const constraints = resolveSectionConstraints(section);

  for (const constraint of constraints) {
    const definition = definitions[constraint.questionTypeId];
    if (definition?.baseType) {
      allowed.add(definition.baseType as QuestionType);
      continue;
    }
    // Legacy / base-type ids stored directly on questionTypes.
    allowed.add(constraint.questionTypeId as QuestionType);
  }

  // Backward compatibility: also accept deprecated questionTypes list.
  for (const type of section.questionTypes ?? []) {
    allowed.add(type);
  }

  return allowed;
}

function questionMatchesSection(
  question: Question,
  section: ExamSection,
  config: ExamTypeConfig
): boolean {
  const allowedBaseTypes = resolveAllowedBaseTypes(section, config);
  const typeMatch = allowedBaseTypes.size === 0 || allowedBaseTypes.has(question.type);
  if (!typeMatch) return false;

  const hasSection = question.section != null && question.section !== '';
  if (hasSection) {
    return question.section === section.id;
  }

  // Legacy questions without section: allow by type only.
  return typeMatch;
}

function resolveSectionSampleLimit(section: ExamSection): number {
  if (typeof section.questionCount === 'number' && section.questionCount > 0) {
    return section.questionCount;
  }

  const constraintCaps = resolveSectionConstraints(section)
    .map((constraint) => constraint.countLimit?.max)
    .filter((value): value is number => typeof value === 'number' && value > 0);
  if (constraintCaps.length > 0) {
    return Math.max(...constraintCaps);
  }

  return UNCONFIGURED_SECTION_QUESTION_CAP;
}

/**
 * 根据考试配置与题库题目生成试卷（模拟考试用）。
 * 按 section 顺序，每个 section 内从符合题型约束且 section 匹配的题目中抽样，顺序固定。
 * 若题目无 section（历史数据或未填），则仅按题型匹配，以兼容旧题。
 *
 * Sampling limit priority:
 * 1. section.questionCount (official mock size)
 * 2. max countLimit across questionTypeConstraints
 * 3. UNCONFIGURED_SECTION_QUESTION_CAP (not an official full paper)
 */
export function buildPaper(
  examTypeId: string,
  config: ExamTypeConfig,
  allQuestions: Question[]
): Paper {
  const sections: PaperSection[] = [];
  const questionIdsUsed = new Set<string>();

  for (const section of config.sections) {
    const candidates = allQuestions.filter((question) => {
      if (questionIdsUsed.has(question.id)) return false;
      return questionMatchesSection(question, section, config);
    });

    const limit = resolveSectionSampleLimit(section);
    const selected = samplePreferringPassageClusters(
      candidates,
      Math.min(limit, candidates.length),
      shuffle
    );
    selected.forEach((question) => questionIdsUsed.add(question.id));

    const constraints = resolveSectionConstraints(section);
    sections.push({
      sectionId: section.id,
      sectionName: section.name,
      durationMinutes: section.duration,
      questionTypes: Array.from(resolveAllowedBaseTypes(section, config)),
      questionTypeConstraintIds: constraints.map((constraint) => constraint.questionTypeId),
      questions: selected,
    });
  }

  const totalDurationMinutes = config.sections.reduce((sum, section) => sum + section.duration, 0);

  return {
    id: `paper_${examTypeId}_${Date.now()}`,
    examTypeId,
    sections,
    totalDurationMinutes,
    createdAt: Date.now(),
  };
}

/**
 * 将 Paper 展平为题目列表（顺序与试卷一致），并返回 section 边界下标，供答题卡分块使用。
 */
export function flattenPaper(paper: Paper): {
  questions: Question[];
  sectionBoundaries: {
    sectionId: string;
    sectionName: string;
    startIndex: number;
    endIndex: number;
  }[];
} {
  const questions: Question[] = [];
  const sectionBoundaries: {
    sectionId: string;
    sectionName: string;
    startIndex: number;
    endIndex: number;
  }[] = [];

  for (const sec of paper.sections) {
    const startIndex = questions.length;
    questions.push(...sec.questions);
    const endIndex = questions.length;
    sectionBoundaries.push({
      sectionId: sec.sectionId,
      sectionName: sec.sectionName,
      startIndex,
      endIndex,
    });
  }

  return { questions, sectionBoundaries };
}

/**
 * 根据试卷与当前答题/标记状态，生成按 section 分块的答题卡数据（模拟考试 UI 用）。
 */
export function buildAnswerSheetBySection(
  paper: Paper,
  answeredSet: Set<number>,
  markedSet: Set<number>
): AnswerSheetBySection {
  let globalIndex = 0;
  const sections = paper.sections.map((sec) => {
    const indices: number[] = [];
    const items = sec.questions.map(() => {
      const index = globalIndex;
      indices.push(index);
      globalIndex += 1;
      return {
        index,
        answered: answeredSet.has(index),
        marked: markedSet.has(index),
      };
    });
    return {
      sectionId: sec.sectionId,
      sectionName: sec.sectionName,
      questionTypes: sec.questionTypes,
      indices,
      items,
    };
  });
  return { sections };
}
